import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { AlertTriangle, GraduationCap, Building2, Sparkles, ChevronDown, ChevronRight, AlertCircle, Users, BookOpen } from "lucide-react";
import { deptAvatarClass } from "@/lib/employees";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function PersonChip({ p, onOpenProfile, extra }) {
  return (
    <button
      onClick={() => onOpenProfile?.(p.id)}
      data-testid={`gap-person-${p.id}`}
      className="inline-flex items-center gap-1.5 pl-1 pr-2 py-0.5 rounded-full text-xs hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors"
      title={`${p.name} — ${p.role}`}
    >
      <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold ${deptAvatarClass(p.department)}`}>
        {p.initials}
      </span>
      <span className="text-zinc-700 dark:text-zinc-300">{p.name.split(" ").slice(0, 2).join(" ")}</span>
      {extra}
    </button>
  );
}

function MentorChip({ mentor, skill, onOpenProfile }) {
  const available = mentor.mentor_status === "available";

  return (
    <div className={`inline-flex items-center gap-1.5 pl-1 pr-1 py-0.5 rounded-full border ${available ? "border-emerald-300 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/30" : "border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30"}`}>
      <button
        onClick={() => onOpenProfile?.(mentor.id)}
        data-testid={`gap-mentor-chip-${mentor.id}`}
        className="inline-flex items-center gap-1.5 hover:opacity-80"
      >
        <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold ${deptAvatarClass(mentor.department)}`}>
          {mentor.initials}
        </span>
        <span className="text-xs font-medium text-zinc-800 dark:text-zinc-200">{mentor.name.split(" ").slice(0, 2).join(" ")}</span>
        <span className={`text-[9px] uppercase tracking-wider font-semibold ${available ? "text-emerald-700 dark:text-emerald-400" : "text-amber-700 dark:text-amber-400"}`}>
          {available ? "Disponibil" : "Ocupat"}
        </span>
      </button>
      {available && mentor.email && (
        <button
          data-testid={`gap-request-${mentor.id}-${skill}`}
          onClick={(e) => { e.stopPropagation(); onOpenProfile?.(mentor.id); }}
          title={`Cere mentorat pe ${skill}`}
          className="inline-flex items-center justify-center h-5 px-2 text-[10px] font-semibold rounded-full bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
        >
          Cere
        </button>
      )}
    </div>
  );
}

export default function GapAnalysis({ onOpenProfile, refreshKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    axios.get(`${API}/insights/gaps`)
      .then((r) => { if (mounted) setData(r.data); })
      .catch((e) => { if (mounted) setError(e.message); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [refreshKey]);

  const items = useMemo(() => {
    if (!data) return [];
    if (filter === "with_mentor") return data.items.filter((i) => i.has_mentor);
    if (filter === "no_mentor") return data.items.filter((i) => !i.has_mentor);
    return data.items;
  }, [data, filter]);

  if (loading) return <div className="py-12 text-center text-zinc-500 dark:text-zinc-400">Se calculează gap-urile...</div>;
  if (error) return <div className="py-12 text-center text-red-600">Eroare: {error}</div>;
  if (!data) return null;

  return (
    <section data-testid="gap-analysis" className="mt-10 space-y-8">
      {/* Mentor pool */}
      {data.mentor_pool.length > 0 && (
        <div>
          <header className="mb-3">
            <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 dark:text-zinc-400 font-medium inline-flex items-center gap-2">
              <GraduationCap className="h-3 w-3" /> Pool de mentori înscriși
            </p>
            <h2 className="mt-1 text-xl sm:text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">
              {data.mentor_pool.length} {data.mentor_pool.length === 1 ? "coleg" : "colegi"} oferă mentorat
            </h2>
          </header>
          <ul data-testid="mentor-pool-list" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.mentor_pool.map((m) => (
              <li key={m.id} data-testid={`mentor-pool-${m.id}`} className="p-3 rounded-xl border border-border bg-white dark:bg-zinc-900 flex items-start gap-3">
                <button onClick={() => onOpenProfile?.(m.id)} className="flex-shrink-0">
                  <span className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${deptAvatarClass(m.department)}`}>
                    {m.initials}
                  </span>
                </button>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <button onClick={() => onOpenProfile?.(m.id)} className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 hover:underline truncate">
                      {m.name}
                    </button>
                    <span className={`inline-flex items-center text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full ${m.mentor_status === "available" ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300" : "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"}`}>
                      {m.mentor_status === "available" ? "Disponibil" : "Ocupat"}
                    </span>
                  </div>
                  <div className="text-[11px] text-zinc-500 dark:text-zinc-400 truncate">{m.role}</div>
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {m.mentor_skills.map((s) => (
                      <span key={s} className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300">
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Mentee pool — colleagues interested in learning */}
      {data.mentee_pool && data.mentee_pool.length > 0 && (
        <div>
          <header className="mb-3">
            <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 dark:text-zinc-400 font-medium inline-flex items-center gap-2">
              <BookOpen className="h-3 w-3" /> Colegi interesați să învețe
            </p>
            <h2 className="mt-1 text-xl sm:text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">
              {data.mentee_pool.length} {data.mentee_pool.length === 1 ? "coleg vrea" : "colegi vor"} să fie mentee
            </h2>
          </header>
          <ul data-testid="mentee-pool-list" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.mentee_pool.map((m) => (
              <li key={m.id} data-testid={`mentee-pool-${m.id}`} className="p-3 rounded-xl border border-emerald-200 dark:border-emerald-900/50 bg-emerald-50/40 dark:bg-emerald-950/10 flex items-start gap-3">
                <button onClick={() => onOpenProfile?.(m.id)} className="flex-shrink-0">
                  <span className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${deptAvatarClass(m.department)}`}>
                    {m.initials}
                  </span>
                </button>
                <div className="min-w-0 flex-1">
                  <button onClick={() => onOpenProfile?.(m.id)} className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 hover:underline truncate block">
                    {m.name}
                  </button>
                  <div className="text-[11px] text-zinc-500 dark:text-zinc-400 truncate">{m.role}</div>
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {m.mentee_skills.map((s) => (
                      <span key={s} className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300">
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}


      {/* Gap items */}
      <div>
        <header className="flex items-end justify-between flex-wrap gap-3 mb-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 dark:text-zinc-400 font-medium inline-flex items-center gap-2">
              <AlertTriangle className="h-3 w-3" /> Risc de succesiune
            </p>
            <h2 className="mt-1 text-xl sm:text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">
              Skills rare ({data.count})
            </h2>
            <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
              Skills cu 1-2 deținători, plus mentorii înscriși și învățăceii sugerați.
            </p>
          </div>
          <div className="inline-flex items-center gap-1 p-1 rounded-full border border-border bg-zinc-50 dark:bg-zinc-900">
            {[
              { k: "all", label: `Toate (${data.count})` },
              { k: "with_mentor", label: `Cu mentor (${data.with_mentor})` },
              { k: "no_mentor", label: `Fără mentor (${data.without_mentor})` },
            ].map((opt) => (
              <button
                key={opt.k}
                data-testid={`gap-filter-${opt.k}`}
                onClick={() => setFilter(opt.k)}
                className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                  filter === opt.k ? "bg-white dark:bg-zinc-700 text-zinc-900 dark:text-zinc-50 shadow-sm" : "text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </header>

        {items.length === 0 ? (
          <div className="py-12 text-center border border-dashed border-border rounded-xl text-zinc-500 dark:text-zinc-400">
            Nicio competență în această categorie.
          </div>
        ) : (
          <ul data-testid="gap-list" className="space-y-3">
            {items.map((i) => {
              const isOpen = expanded[i.skill] !== false;
              return (
                <li
                  key={i.skill}
                  data-testid={`gap-item-${i.skill}`}
                  className={`rounded-xl border bg-white dark:bg-zinc-900 overflow-hidden ${i.has_mentor ? "border-border" : "border-amber-300 dark:border-amber-900/60"}`}
                >
                  <button
                    onClick={() => setExpanded((s) => ({ ...s, [i.skill]: !isOpen }))}
                    className="w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-zinc-50 dark:hover:bg-zinc-800/40 transition-colors"
                  >
                    {isOpen ? <ChevronDown className="h-4 w-4 text-zinc-400" /> : <ChevronRight className="h-4 w-4 text-zinc-400" />}
                    <Sparkles className="h-4 w-4 text-zinc-500" />
                    <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 flex-1">{i.skill}</h3>
                    <span className={`inline-flex items-center text-[10px] uppercase tracking-wider font-medium px-2 py-0.5 rounded-full ${i.holder_count === 1 ? "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300" : "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"}`}>
                      {i.holder_count} {i.holder_count === 1 ? "deținător" : "deținători"}
                    </span>
                    {i.has_mentor ? (
                      <span className="hidden sm:inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-medium px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300">
                        {i.mentors.length} mentor{i.mentors.length > 1 ? "i" : ""}
                      </span>
                    ) : (
                      <span className="hidden sm:inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                        <AlertTriangle className="h-3 w-3" /> Fără mentor
                      </span>
                    )}
                  </button>

                  {isOpen && (
                    <div className="px-5 pb-5 pt-1 space-y-3 border-t border-border">
                      {/* Mentors opt-in */}
                      <div>
                        <h4 className="text-[11px] uppercase tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400 mb-1.5 inline-flex items-center gap-1.5">
                          <GraduationCap className="h-3 w-3" />
                          {i.has_mentor ? "Mentori înscriși" : "Niciun mentor înscris încă"}
                        </h4>
                        {i.has_mentor ? (
                          <div className="flex flex-wrap gap-1.5">
                            {i.mentors.map((m) => (
                              <MentorChip
                                key={m.id}
                                mentor={m}
                                skill={i.skill}
                                onOpenProfile={onOpenProfile}
                              />
                            ))}
                          </div>
                        ) : (
                          <p className="text-[11px] text-amber-700 dark:text-amber-400 inline-flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" /> Niciun coleg nu s-a înscris ca mentor pe „{i.skill}". Invită-l pe un deținător din lista de mai jos.
                          </p>
                        )}
                      </div>

                      {/* Interested mentees (explicit opt-in via mentee_skills) */}
                      {i.interested_mentees.length > 0 && (
                        <div>
                          <h4 className="text-[11px] uppercase tracking-[0.2em] font-medium text-emerald-700 dark:text-emerald-400 mb-1.5 inline-flex items-center gap-1.5">
                            <BookOpen className="h-3 w-3" /> Colegi interesați să învețe ({i.interested_mentees.length})
                          </h4>
                          <div className="flex flex-wrap gap-1.5">
                            {i.interested_mentees.map((p) => (
                              <PersonChip key={p.id} p={p} onOpenProfile={onOpenProfile} />
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Holders (informational) */}
                      <div>
                        <h4 className="text-[11px] uppercase tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400 mb-1.5 inline-flex items-center gap-1.5">
                          <Users className="h-3 w-3" /> Deținători actuali ({i.holder_count})
                        </h4>
                        <div className="flex flex-wrap gap-1.5">
                          {i.holders.map((p) => (
                            <PersonChip
                              key={p.id}
                              p={p}
                              onOpenProfile={onOpenProfile}
                              extra={p.is_management && <span className="text-[9px] uppercase tracking-wider text-emerald-700 dark:text-emerald-400 font-semibold">mgmt</span>}
                            />
                          ))}
                        </div>
                      </div>

                      {/* Learners — same department */}
                      {i.learners_department.length > 0 && (
                        <div>
                          <h4 className="text-[11px] uppercase tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400 mb-1.5 inline-flex items-center gap-1.5">
                            <Building2 className="h-3 w-3" /> Învățăcei din același departament
                          </h4>
                          <div className="flex flex-wrap gap-1.5">
                            {i.learners_department.map((p) => (
                              <PersonChip key={p.id} p={p} onOpenProfile={onOpenProfile} />
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Learners — shared skills */}
                      {i.learners_shared.length > 0 && (
                        <div>
                          <h4 className="text-[11px] uppercase tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400 mb-1.5 inline-flex items-center gap-1.5">
                            <Sparkles className="h-3 w-3" /> Învățăcei cu skill-uri înrudite
                          </h4>
                          <div className="flex flex-wrap gap-1.5">
                            {i.learners_shared.map((p) => (
                              <PersonChip key={p.id} p={p} onOpenProfile={onOpenProfile} />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}
